<template>
  <div class="roster-page">



    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>

    <div class="container">
      <div class="page-layout">

        <!-- 左侧：3D 只读预览（不可编辑，不可缩放） -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>

        <!-- 右侧：名单 -->
        <div class="right-panel">
          <div class="panel-block">
              <LogoUploader
                ref="logoUploader"
                :current-selected-part="'front'"
                @add-image="handleAddImageToPart"
              />
          </div>
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" />
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { useDesignStore } from '@/stores/designStore'
import { mapActions } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import RosterManager from '@/components/RosterManager.vue'
import LogoUploader from '@/components/LogoUploader.vue'

export default {
  name: 'tshirtroster',
  components: { Viewer3D, RosterManager, LogoUploader },

  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    ...mapActions(useDesignStore, ['loadDesignFromData']),

    async handleAddImageToPart({ url, part, imageId }) {
      const store = useDesignStore()
      const newLayer = await store.addImageToPart(part, url, imageId)
      if (newLayer && newLayer.id && this.$refs.viewer3d) {
        this.$refs.viewer3d.compositePartTexture(part, newLayer.id)
      }
    },
    async init() {
      const params = new URLSearchParams(window.location.search)
      const id = params.get('id')
      if (!id) {
        // 没有 design id，直接显示默认款式
        await this.$nextTick()
        if (this.$refs.viewer3d) this.$refs.viewer3d.loadGarment('T-shirt')
        return
      }

      this.isLoading = true
      const store = useDesignStore()
      
      try {
        // 固定加载 T-shirt 款式
        await store.loadSharedDesign(id, {
          targetGarment: 'T-shirt',  // 固定为 T-shirt
          loadComments: false,             // 不需要评论
        })
        
        // 加载 3D 模型
        if (this.$refs.viewer3d) {
          this.$refs.viewer3d.loadGarment('T-shirt')
        }
        
        // 刷新 RosterManager 显示
        await this.$nextTick()
        if (this.$refs.rosterManager?.loadFromStore) {
          this.$refs.rosterManager.loadFromStore()
        }
        
      } catch (e) {
        console.error('Load error:', e)
        this.showToast('Failed to load design', true)
      } finally {
        this.isLoading = false
      }
    },

    // 模型加载完成后触发，此时 partsMap 已建立，可以合成纹理
    async onModelLoaded() {
      const store = useDesignStore()
      const viewer = this.$refs.viewer3d
      if (!viewer) return

      // 先恢复颜色
      if (typeof viewer.applyColorsToModel === 'function') {
        viewer.applyColorsToModel()
      }

      // 再合成每个有图层的 part 的纹理（logo + 文字）
      for (const [part, layers] of store.partLayersMap.entries()) {
        if (!layers || layers.length === 0) continue
        if (!viewer.partsMap || !viewer.partsMap.has(part)) continue

        // 预加载图片（logo）
        for (const layer of layers) {
          if (!layer.isText && layer.url && !layer.img) {
            await new Promise((resolve) => {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.onload = () => { layer.img = img; resolve() }
              img.onerror = () => resolve()
              img.src = layer.url
            })
          }
          // 文字图层：生成 canvas 图像
          if (layer.isText && layer.textContent && !layer.img) {
            const canvas = store._createTextCanvas(
              layer.textContent,
              layer.textFont || 'Arial',
              layer.textColor || '#000000',
              layer.fontSize || 80
            )
            const img = new Image()
            img.src = canvas.toDataURL()
            layer.img = img
          }
        }

        // 合成纹理
        viewer.compositePartTexture(part, null, false)
      }
    },

    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },
  },
}
</script>

<style scoped>
.roster-page {
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #333;
  min-height: 100vh;
}
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.loading-inner { text-align: center; color: #fff; }
.spinner {
  width: 40px; height: 40px;
  border: 4px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }
.toast {
  position: fixed; bottom: 24px; left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px; border-radius: 8px;
  font-size: 0.85rem; font-weight: 500;
  color: #fff; z-index: 10001; pointer-events: none;
}
.toast-success { background: #10b981; }
.toast-error   { background: #ef4444; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
.container {
  width: 95%; max-width: 1400px;
  margin: 0 auto; padding: 1rem 0;
}
.page-layout {
  display: flex; gap: 2rem;
  flex-wrap: wrap; align-items: flex-start;
}
.viewer-wrapper { flex: 1; min-width: 280px; }
.right-panel {
  flex: 1; min-width: 320px;
  display: flex; flex-direction: column; gap: 0.8rem;
}
.panel-block { background: #f5f5f5; border-radius: 10px; padding: 1rem; }
.roster-block { background: #f0fdf4; }
@media (max-width: 900px) {
  .page-layout { flex-direction: column; }
  .viewer-wrapper, .right-panel { width: 100%; min-width: 0; }
}
@media (max-width: 850px) {
  .container { width: 100%; padding: 0.5rem; }
  .panel-block { padding: 0.6rem; }
}
</style>